import { useState, useEffect, Suspense, lazy, useRef } from 'react'

import {
  AddCommentModal,
  ConfirmDeleteCommentModal,
  ConfirmDeleteGroupModal
} from '../../plan/group'
import {
  ConfirmClearPlanModal,
  ConfirmDeletePlanModal,
  ConfirmReplacePrimaryModal,
  ScheduleCompletedCoursesModal
} from '../../plan/board'
import DownloadStudentUsageModal from '../../reporting/studentUsageReport/DownloadStudentUsageModal'
import EmailStudentModal from '../../student/EmailStudentModal'
import { AcceptableUseModal, TermsOfServiceModal } from '../../tos'
import {
  ConfirmApplyTemplateInactiveCourseModal,
  ConfirmArchiveTemplateModal,
  ConfirmDeleteTemplateModal,
  ConfirmPublishTemplateModal
} from '../../templates/planTemplates/planTemplateModals'
import ConfirmLockPublishedTemplatesModalWrapper from '../../templates/templateModals/ConfirmLockPublishedTemplatesModal/ConfirmLockPublishedTemplatesModalWrapper'
import ConfirmReadOnlyBulkActionModalWrapper from '../../templates/templateModals/ConfirmReadOnlyBulkActionModal/ConfirmReadOnlyBulkActionModalWrapper'
import ConfirmCopyPublishedTemplatesModalWrapper from '../../templates/templateModals/ConfirmCopyPublishedTemplatesModal/ConfirmCopyPublishedTemplatesModalWrapper'

import useModal from '../../../lib/hooks/useModal'
import {
  ModalAddCommentData,
  ModalApplyTemplateData,
  ModalDeleteGroupData,
  ModalDeleteTemplateData,
  ModalEmailStudentData,
  ModalIdData,
  ModalMarkCommentsAsReadData,
  ModalMoveGroupData,
  ModalName,
  ModalOwnerIdData,
  ModalPayload,
  ModalPlanItemDetailsData,
  ModalScheduleCompletedCoursesData
} from '../../../types'
import MoveGroupModal from '../../plan/group/MoveGroupModal'
import {
  ConfirmArchivePlaceholderModal,
  ConfirmDeletePlaceholderModal,
  ConfirmPublishPlaceholderModal
} from '../../templates/placeholderTemplates/placeholderTemplateModals'
import MarkCommentsAsReadModal from '../../dashboard/engagement/MarkCommentsAsReadModal/MarkCommentsAsReadModalWrapper'

// PlanItemDetailsModal can include a heavy rich text editor, so lazily load to
// reduce bundle size on app
const PlanItemDetailsModal = lazy(() => import('../../plan/item/PlanItemDetailsModal'))

// inspiration from https://blog.isquaredsoftware.com/2017/07/practical-redux-part-10-managing-modals/
const ModalManager = () => {
  const [modalState, setModalState] = useState<ModalPayload | undefined>(undefined)
  const [isOpen, setIsOpen] = useState(true)
  const { modalPayload } = useModal()
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (modalPayload && modalPayload.name) {
      if (isMounted.current) {
        setModalState(modalPayload)
        setIsOpen(true)
      }
      return undefined
    }

    // trick to allow the closing animation
    // see for details: https://github.com/reactjs/react-modal/issues/334
    if (isMounted.current) {
      setIsOpen(false)
    }

    const timeoutId = setTimeout(() => {
      if (isMounted.current) {
        setModalState(undefined)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [modalPayload])

  if (!modalState) {
    return null
  }

  const getId = () => {
    if (!modalState.data) {
      return ''
    }
    if (modalState.data as ModalIdData) {
      return (modalState.data as ModalIdData).id
    }
    if (modalState.data as ModalOwnerIdData) {
      return (modalState.data as ModalOwnerIdData).id
    }
    return ''
  }
  const getOwnerId = () =>
    modalState.data && (modalState.data as ModalOwnerIdData)
      ? (modalState.data as ModalOwnerIdData).ownerAppUserId
      : 0

  const getModal = () => {
    switch (modalState.name) {
      case ModalName.AcceptableUse:
        return <AcceptableUseModal isOpen={isOpen} />
      case ModalName.AddComment: {
        const payload = modalState.data && (modalState.data as ModalAddCommentData)
        if (!payload) {
          return null
        }
        return (
          <AddCommentModal
            groupId={payload!.groupId.toString()}
            isOpen={isOpen}
            isTemplate={payload.isTemplate}
            readOnly={payload.readOnly}
            showAddComment={payload.showAddComment!}
            showComments={payload.showComments!}
            showNewCommentIndicator={payload.showNewCommentIndicator!}
          />
        )
      }
      case ModalName.ConfirmApplyTemplateInactiveCourse: {
        const payload = modalState.data && (modalState.data as ModalApplyTemplateData)
        return (
          <ConfirmApplyTemplateInactiveCourseModal
            id={getId()}
            inactiveCourses={payload?.inactiveCourses || []}
            startTermCode={payload?.startTermCode || ''}
            isOpen={isOpen}
            search={payload?.search || ''}
            studentExternalIds={payload?.studentExternalIds || []}
            templateMessage={payload?.templateMessage || ''}
          />
        )
      }
      case ModalName.ConfirmArchiveTemplate:
        return <ConfirmArchiveTemplateModal id={getId()} isOpen={isOpen} />
      case ModalName.ConfirmPublishTemplate:
        return <ConfirmPublishTemplateModal id={getId()} isOpen={isOpen} />
      case ModalName.ConfirmLockPublishedTemplates:
        return <ConfirmLockPublishedTemplatesModalWrapper isOpen={isOpen} />
      case ModalName.ConfirmReadOnlyBulkAction:
        return <ConfirmReadOnlyBulkActionModalWrapper isOpen={isOpen} />
      case ModalName.ConfirmCopyPublishedTemplates:
        return <ConfirmCopyPublishedTemplatesModalWrapper isOpen={isOpen} />
      case ModalName.ConfirmDeleteComment:
        return <ConfirmDeleteCommentModal id={getId()} isOpen={isOpen} />
      case ModalName.ConfirmDeleteGroup: {
        const payload = modalState.data && (modalState.data as ModalDeleteGroupData)
        return (
          <ConfirmDeleteGroupModal
            id={getId()}
            isOpen={isOpen}
            isTemplate={payload?.isTemplate || false}
            planId={payload?.planId || ''}
          />
        )
      }
      case ModalName.ConfirmDeletePlan:
        return <ConfirmDeletePlanModal id={getId()} isOpen={isOpen} ownerAppUserId={getOwnerId()} />
      case ModalName.ConfirmArchivePlaceholder:
        return <ConfirmArchivePlaceholderModal id={getId()} isOpen={isOpen} />
      case ModalName.ConfirmPublishPlaceholder:
        return <ConfirmPublishPlaceholderModal id={getId()} isOpen={isOpen} />
      case ModalName.ConfirmDeleteTemplate: {
        const payload = modalState.data && (modalState.data as ModalDeleteTemplateData)
        return (
          <ConfirmDeleteTemplateModal id={getId()} isOpen={isOpen} search={payload?.search || ''} />
        )
      }
      case ModalName.ConfirmDeletePlaceholder: {
        const payload = modalState.data && (modalState.data as ModalDeleteTemplateData)
        return (
          <ConfirmDeletePlaceholderModal
            id={getId()}
            isOpen={isOpen}
            search={payload?.search || ''}
          />
        )
      }
      case ModalName.ConfirmReplacePrimary:
        return (
          <ConfirmReplacePrimaryModal id={getId()} isOpen={isOpen} ownerAppUserId={getOwnerId()} />
        )
      case ModalName.ConfirmClearPlan:
        return <ConfirmClearPlanModal id={getId()} isOpen={isOpen} />
      case ModalName.DownloadStudentUsage:
        return <DownloadStudentUsageModal isOpen={isOpen} />
      case ModalName.EmailStudent: {
        const payload = modalState.data && (modalState.data as ModalEmailStudentData)
        const toEmailAddress = payload?.toEmailAddress || ''
        return <EmailStudentModal isOpen={isOpen} toEmailAddress={toEmailAddress} />
      }
      case ModalName.MarkCommentsAsRead: {
        const payload = modalState.data && (modalState.data as ModalMarkCommentsAsReadData)
        const advisorExternalId = payload?.advisorExternalId || ''
        const allStudentsCount = payload?.allStudentsCount || 0
        const includeAllStudents = payload?.includeAllStudents
        const selectedStudentsCount = payload?.selectedStudentsCount || 0
        const unselectedStudentIds = payload?.unselectedStudentIds || []
        const selectedStudentIds = payload?.selectedStudentIds || []
        const onMarkCommentsCompleted = payload?.onMarkCommentsCompleted

        return (
          <MarkCommentsAsReadModal
            isOpen={isOpen}
            advisorExternalId={advisorExternalId}
            allStudentsCount={allStudentsCount}
            includeAllStudents={!!includeAllStudents}
            selectedStudentsCount={selectedStudentsCount}
            unselectedStudentIds={unselectedStudentIds}
            selectedStudentIds={selectedStudentIds}
            onMarkCommentsCompleted={onMarkCommentsCompleted || (() => {})}
          />
        )
      }
      case ModalName.MovePlanGroup: {
        const payload = modalState.data && (modalState.data as ModalMoveGroupData)
        const groupId = payload?.groupId || ''
        const planId = payload?.planId || ''
        const isTemplate = payload?.isTemplate || false
        return (
          <MoveGroupModal
            isOpen={isOpen}
            groupId={groupId}
            planId={planId}
            isTemplate={isTemplate}
          />
        )
      }
      case ModalName.PlanItemDetails: {
        const payload = modalState.data && (modalState.data as ModalPlanItemDetailsData)
        const id = payload?.id || ''
        const campus = payload?.campus || ''
        const isTemplate = payload?.isTemplate || false
        const planId = payload?.planId || ''
        const readOnly = payload?.readOnly || false
        const disableDelete = payload?.disableDelete || false
        const disableGroupChange = payload?.disableGroupChange || false
        return (
          <PlanItemDetailsModal
            id={id}
            campus={campus}
            disableDelete={disableDelete}
            disableGroupChange={disableGroupChange}
            isTemplate={isTemplate}
            planId={planId}
            isOpen={isOpen}
            readOnly={readOnly}
          />
        )
      }
      case ModalName.ScheduleCompletedCourses: {
        const payload = modalState.data && (modalState.data as ModalScheduleCompletedCoursesData)
        const completedCoursesData = payload?.completedCoursesData || []
        return (
          <ScheduleCompletedCoursesModal
            completedCoursesData={completedCoursesData}
            isOpen={isOpen}
            ownerAppUserId={getOwnerId()}
          />
        )
      }
      case ModalName.TermsOfService:
        return <TermsOfServiceModal isOpen={isOpen} />
      default:
        return null
    }
  }

  return <Suspense fallback={<div aria-hidden />}>{getModal()}</Suspense>
}

export default ModalManager
